import React, { Suspense, useContext, useState, useMemo, lazy, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Layout, Typography, Space, Dropdown, Button, Grid, Drawer, Menu } from "antd";
import {
  DownOutlined,
  PlusOutlined,
  UserOutlined,
  CarOutlined,
  LineChartOutlined,
  CalendarOutlined,
  FlagOutlined,
  SettingOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  SafetyCertificateOutlined,
  FileProtectOutlined,
  MenuOutlined,
  LogoutOutlined,
  RightOutlined,
  ArrowLeftOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  PlaySquareOutlined,
  PoweroffOutlined,
  BgColorsOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { AuthContext } from '../../contexts/AuthContext';
import { DataContext } from '../../contexts/DataContext';
import { Can, useAbility } from '../../casl/AbilityContext';
import { ACTIONS } from '../../casl/actions';
import { SUBJECTS } from '../../casl/subjects';
import { ADMIN_PATH } from '../../api/companySession';
import { useLogout } from '../../pages/Logout';
import LoggedUserBadge from './LoggedUserBadge';
import { useAppTheme } from '../../contexts/ThemeContext';
import { THEME_OPTIONS } from '../../theme';

const AddDriverForm = lazy(() => import('../drivers/AddDriverForm'));
const DriversList = lazy(() => import('../drivers/DriversList'));
const DriversDeactivateList = lazy(() => import('../drivers/DriversDeactivateList'));
const AddCarForm = lazy(() => import('../cars/AddCarForm'));
const CarsList = lazy(() => import('../cars/CarsList'));
const DriverAnalytics = lazy(() => import('../admin/DriverAnalytics'));
const HistoryAnalytics = lazy(() => import('../admin/HistoryAnalytics'));
const RequestDrive = lazy(() => import('../drive/RequestDrive'));
const SetupConfiguration = lazy(() => import('../admin/SetupConfiguration'));
const TariffsModal = lazy(() => import('../admin/TariffsPanel'));
const AdsModal = lazy(() => import('../ads/AdsPanel'));
const RolesManagement = lazy(() => import('../roles/RolesManagement'));
const PermissionsManagement = lazy(() => import('../permissions/PermissionsManagement'));
const FeatureFlagsManagement = lazy(() => import('../featureFlags/FeatureFlagsManagement'));
const PrivacyRequestsList = lazy(() => import('../privacy/PrivacyRequestsList'));
const AddUserForm = lazy(() => import('../users/AddUserForm'));
const AssignUserRole = lazy(() => import('../users/AssignUserRole'));
const UsersList = lazy(() => import('../users/UsersList'));
const AddStandForm = lazy(() => import('../stands/AddStandForm'));
const StandsList = lazy(() => import('../stands/StandsList'));

const { Header: AntHeader } = Layout;
const { Title } = Typography;
const { useBreakpoint } = Grid;

const Header = () => {
  const {
    isAuthenticated,
    isAuthReady,
    isPlatformSupervisor,
    isInCompanyContext,
    activeCompanyName,
    currentUser,
    exitCompanyContext,
  } = useContext(AuthContext);
  const { isLiveMapDemoActive } = useContext(DataContext);
  const { themeKey, setThemeKey } = useAppTheme();
  const ability = useAbility();
  const { confirmLogout } = useLogout();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isCompact = !screens.lg;
  const isAdminPage = location.pathname === ADMIN_PATH;

  const goBackToCompanies = useCallback(async () => {
    await exitCompanyContext();
    navigate(ADMIN_PATH, { replace: true });
  }, [exitCompanyContext, navigate]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [showDriversList, setShowDriversList] = useState(false);
  const [showDeactivateDriversList, setShowDeactivateDriversList] = useState(false);

  const [showAddCar, setShowAddCar] = useState(false);
  const [showCarsList, setShowCarsList] = useState(false);
  const [showAddStand, setShowAddStand] = useState(false);
  const [showStandsList, setShowStandsList] = useState(false);
  const [showDriverAnalytics, setShowDriverAnalytics] = useState(false);
  const [showHistoryAnalytics, setShowHistoryAnalytics] = useState(false);
  const [showRequestDrive, setShowRequestDrive] = useState(false);
  const [showSetupConfiguration, setShowSetupConfiguration] = useState(false);
  const [showTariffs, setShowTariffs] = useState(false);
  const [showAds, setShowAds] = useState(false);
  const [showRolesManagement, setShowRolesManagement] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showUsersList, setShowUsersList] = useState(false);
  const [showAssignUserRole, setShowAssignUserRole] = useState(false);
  const [showPermissionsManagement, setShowPermissionsManagement] = useState(false);
  const [showFeatureFlagsManagement, setShowFeatureFlagsManagement] = useState(false);
  const [showPrivacyRequests, setShowPrivacyRequests] = useState(false);

  const runMenuAction = useCallback((action) => {
    setMenuOpen(false);
    action?.();
  }, []);

  const driverMenuItems = useMemo(
    () =>
      [
        ability.can(ACTIONS.CREATE, SUBJECTS.DRIVER) && {
          key: "addDriver",
          icon: <PlusOutlined />,
          label: "Dodaj vozača",
          onClick: () => runMenuAction(() => setShowAddDriver(true)),
        },
        ability.can(ACTIONS.READ, SUBJECTS.DRIVER) && {
          key: "listDrivers",
          icon: <UserOutlined />,
          label: "Lista vozača",
          onClick: () => runMenuAction(() => setShowDriversList(true)),
        },
        ability.can(ACTIONS.DELETE, SUBJECTS.DRIVER) && {
          key: "deactivateDrivers",
          icon: <PoweroffOutlined />,
          label: "Odjava vozača",
          onClick: () => runMenuAction(() => setShowDeactivateDriversList(true)),
        },
      ].filter(Boolean),
    [ability, runMenuAction]
  );

  const usersAndRolesMenuItems = useMemo(
    () =>
      [
        ability.can(ACTIONS.CREATE, SUBJECTS.WEB_USERS) && {
          key: "addUser",
          icon: <PlusOutlined />,
          label: "Dodaj korisnika",
          onClick: () => runMenuAction(() => setShowAddUser(true)),
        },
        ability.can(ACTIONS.READ, SUBJECTS.WEB_USERS) && {
          key: "listUsers",
          icon: <UnorderedListOutlined />,
          label: "Lista korisnika",
          onClick: () => runMenuAction(() => setShowUsersList(true)),
        },
        ability.can(ACTIONS.UPDATE, SUBJECTS.WEB_USERS) && {
          key: "assignUserRole",
          icon: <SafetyCertificateOutlined />,
          label: "Dodjela role korisniku",
          onClick: () => runMenuAction(() => setShowAssignUserRole(true)),
        },
        ability.can(ACTIONS.UPDATE, SUBJECTS.WEB_USERS) && {
          key: "roles",
          icon: <SafetyCertificateOutlined />,
          label: "Upravljanje rolama",
          onClick: () => runMenuAction(() => setShowRolesManagement(true)),
        },
      ].filter(Boolean),
    [ability, runMenuAction]
  );

  const carMenuItems = useMemo(
    () =>
      [
        ability.can(ACTIONS.CREATE, SUBJECTS.CAR) && {
          key: "addCar",
          icon: <PlusOutlined />,
          label: "Dodaj vozilo",
          onClick: () => runMenuAction(() => setShowAddCar(true)),
        },
        ability.can(ACTIONS.READ, SUBJECTS.CAR) && {
          key: "listCars",
          icon: <CarOutlined />,
          label: "Lista vozila",
          onClick: () => runMenuAction(() => setShowCarsList(true)),
        },
      ].filter(Boolean),
    [ability, runMenuAction]
  );

  const standMenuItems = useMemo(
    () =>
      [
        ability.can(ACTIONS.CREATE, SUBJECTS.STAND) && {
          key: "addStand",
          icon: <PlusOutlined />,
          label: "Dodaj štand",
          onClick: () => runMenuAction(() => setShowAddStand(true)),
        },
        ability.can(ACTIONS.READ, SUBJECTS.STAND) && {
          key: "listStands",
          icon: <EnvironmentOutlined />,
          label: "Lista štandova",
          onClick: () => runMenuAction(() => setShowStandsList(true)),
        },
      ].filter(Boolean),
    [ability, runMenuAction]
  );

  const mainMenuItems = useMemo(() => {
    const items = [];

    if (isAdminPage && isPlatformSupervisor) {
      items.push({
        key: "permissions",
        icon: <SafetyCertificateOutlined />,
        label: "Permisije",
        onClick: () => runMenuAction(() => setShowPermissionsManagement(true)),
      });
      items.push({
        key: "featureFlags",
        icon: <FlagOutlined />,
        label: "Feature flagovi",
        onClick: () => runMenuAction(() => setShowFeatureFlagsManagement(true)),
      });
      items.push({
        key: "privacyRequests",
        icon: <FileProtectOutlined />,
        label: "Zahtjevi za brisanje podataka",
        onClick: () => runMenuAction(() => setShowPrivacyRequests(true)),
      });
    }

    if (!isAdminPage) {
      if (ability.can(ACTIONS.ACCESS, SUBJECTS.CONFIGURATION)) {
        items.push({
          key: "configuration",
          icon: <SettingOutlined />,
          label: "Konfiguracija",
          popupClassName: "app-header__submenu-popup",
          children: [
            {
              key: "settings",
              icon: <SettingOutlined />,
              label: "Postavke",
              onClick: () => runMenuAction(() => setShowSetupConfiguration(true)),
            },
            {
              key: "tariffs",
              icon: <DollarOutlined />,
              label: "Tarife",
              onClick: () => runMenuAction(() => setShowTariffs(true)),
            },
          ],
        });
      }

      if (usersAndRolesMenuItems.length > 0) {
        items.push({
          key: "usersAndRoles",
          icon: <TeamOutlined />,
          label: "Korisnici i role",
          children: usersAndRolesMenuItems,
          popupClassName: "app-header__submenu-popup",
        });
      }

      if (driverMenuItems.length > 0) {
        items.push({
          key: "drivers",
          icon: <UserOutlined />,
          label: "Vozači",
          children: driverMenuItems,
          popupClassName: "app-header__submenu-popup",
        });
      }

      if (carMenuItems.length > 0) {
        items.push({
          key: "cars",
          icon: <CarOutlined />,
          label: "Vozila",
          children: carMenuItems,
          popupClassName: "app-header__submenu-popup",
        });
      }

      if (standMenuItems.length > 0) {
        items.push({
          key: "stands",
          icon: <EnvironmentOutlined />,
          label: "Štandovi",
          children: standMenuItems,
          popupClassName: "app-header__submenu-popup",
        });
      }

      if (ability.can(ACTIONS.ACCESS, SUBJECTS.AD)) {
        items.push({
          key: "ads",
          icon: <PlaySquareOutlined />,
          label: "Reklame",
          onClick: () => runMenuAction(() => setShowAds(true)),
        });
      }

      if (ability.can(ACTIONS.READ, SUBJECTS.STATISTICS)) {
        items.push({
          key: "analytics",
          icon: <LineChartOutlined />,
          label: "Analitika",
          popupClassName: "app-header__submenu-popup",
          children: [
            {
              key: "driverAnalytics",
              icon: <LineChartOutlined />,
              label: "Analitika vozača",
              onClick: () => runMenuAction(() => setShowDriverAnalytics(true)),
            },
            {
              key: "historyAnalytics",
              icon: <CalendarOutlined />,
              label: "Mjesečna analitika",
              onClick: () => runMenuAction(() => setShowHistoryAnalytics(true)),
            },
          ],
        });
      }
    }

    items.push({
      key: "theme",
      icon: <BgColorsOutlined />,
      label: "Tema",
      popupClassName: "app-header__submenu-popup",
      children: THEME_OPTIONS.map((option) => ({
        key: `theme-${option.key}`,
        icon: (
          <span
            className="app-header__theme-swatch"
            style={{ background: option.swatch }}
            aria-hidden
          />
        ),
        label: (
          <span className="app-header__theme-option">
            <span className="app-header__theme-text">
              <span>{option.label}</span>
              <span className="app-header__theme-description">{option.description}</span>
            </span>
            {option.key === themeKey && <CheckOutlined className="app-header__theme-check" />}
          </span>
        ),
        onClick: () => runMenuAction(() => setThemeKey(option.key)),
      })),
    });

    items.push({ type: "divider", key: "divider-before-logout" });

    items.push({
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Odjava",
      danger: true,
      onClick: () => runMenuAction(confirmLogout),
    });

    return items;
  }, [
    ability,
    carMenuItems,
    confirmLogout,
    driverMenuItems,
    isAdminPage,
    isPlatformSupervisor,
    runMenuAction,
    setThemeKey,
    standMenuItems,
    themeKey,
    usersAndRolesMenuItems,
  ]);

  const renderMenuTrigger = () => {
    if (isCompact) {
      return (
        <>
          <Button
            type="default"
            size="large"
            className="app-header__menu-button"
            icon={<MenuOutlined />}
            aria-label="Meni"
            onClick={() => setMenuOpen(true)}
          />
          <Drawer
            title="Meni"
            placement="right"
            width="100%"
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            className="app-header__menu-drawer"
            destroyOnHidden
          >
            <Menu
              mode="inline"
              selectable={false}
              items={mainMenuItems}
              className="app-header__drawer-menu"
            />
          </Drawer>
        </>
      );
    }

    return (
      <Dropdown
        menu={{
          items: mainMenuItems,
          className: "app-header__menu",
          expandIcon: <RightOutlined className="app-header__menu-arrow" />,
        }}
        placement="bottomRight"
        arrow={{ pointAtCenter: true }}
        trigger={["click"]}
        popupRender={(menu) => (
          <div className="app-header__menu-popup">{menu}</div>
        )}
      >
        <Button
          type="default"
          size="large"
          className="app-header__menu-button"
          icon={<MenuOutlined />}
          aria-label="Meni"
        >
          Meni <DownOutlined />
        </Button>
      </Dropdown>
    );
  };

  const headerTitle = isAdminPage
    ? isCompact
      ? "Kompanije"
      : "Administracija kompanija"
    : activeCompanyName
      ? isCompact
        ? activeCompanyName
        : `Kontrolna tabla — ${activeCompanyName}`
      : null;

  const showBackToCompanies =
    isAuthenticated &&
    isAuthReady &&
    !isAdminPage &&
    isPlatformSupervisor &&
    isInCompanyContext;

  return (
    <AntHeader className={`app-header${isCompact ? " app-header--compact" : ""}`}>
      <div className="app-header__brand">
        {showBackToCompanies && (
          <Button
            type="default"
            size="large"
            className="app-header__back"
            icon={<ArrowLeftOutlined />}
            onClick={() => goBackToCompanies()}
            aria-label="Nazad na kompanije"
            title="Nazad na kompanije"
          >
            {isCompact ? null : "Nazad"}
          </Button>
        )}
        {isAuthenticated && isAuthReady && currentUser && (
          <LoggedUserBadge
            user={currentUser}
            companyName={activeCompanyName}
            showGreeting={!activeCompanyName || isAdminPage}
          />
        )}
        {isAuthenticated && isAuthReady && !currentUser && !isAdminPage && !activeCompanyName && (
          <span className="app-header__welcome">Dobrodošli</span>
        )}
        {headerTitle && (
          <Title
            level={isCompact ? 4 : 3}
            className="app-header__title"
            title={headerTitle}
          >
            {headerTitle}
          </Title>
        )}
      </div>

      <Space className="app-header__actions" size={isCompact ? "small" : "middle"}>
        {isAuthenticated && isAuthReady && (
          <>
            {!isAdminPage && (
              <Can I={ACTIONS.CREATE} a={SUBJECTS.DRIVE}>
                <Button
                  type="primary"
                  size="large"
                  icon={<FlagOutlined />}
                  onClick={() => setShowRequestDrive(true)}
                  className="app-header__create-drive"
                  aria-label="Kreiraj vožnju"
                  // Demo drivers exist only in the browser; a drive for one would reach the API.
                  disabled={isLiveMapDemoActive}
                >
                  {isCompact ? "Vožnja" : "Kreiraj vožnju"}
                </Button>
              </Can>
            )}

            {renderMenuTrigger()}
          </>
        )}
      </Space>

      {isAuthenticated && isAuthReady && !isAdminPage && (
        <Suspense fallback={null}>
          <Can I={ACTIONS.CREATE} a={SUBJECTS.DRIVER}>
            {showAddDriver && (
              <AddDriverForm
                isVisible={showAddDriver}
                onClose={() => setShowAddDriver(false)}
              />
            )}
          </Can>
          <Can I={ACTIONS.READ} a={SUBJECTS.DRIVER}>
            {showDriversList && (
              <DriversList
                isVisible={showDriversList}
                onClose={() => setShowDriversList(false)}
              />
            )}
          </Can>
          <Can I={ACTIONS.DELETE} a={SUBJECTS.DRIVER}>
            {showDeactivateDriversList && (
              <DriversDeactivateList
                isVisible={showDeactivateDriversList}
                onClose={() => setShowDeactivateDriversList(false)}
              />
            )}
          </Can>

          <Can I={ACTIONS.CREATE} a={SUBJECTS.CAR}>
            {showAddCar && (
              <AddCarForm
                isVisible={showAddCar}
                onClose={() => setShowAddCar(false)}
              />
            )}
          </Can>
          <Can I={ACTIONS.READ} a={SUBJECTS.CAR}>
            {showCarsList && (
              <CarsList
                isVisible={showCarsList}
                onClose={() => setShowCarsList(false)}
              />
            )}
          </Can>

          <Can I={ACTIONS.CREATE} a={SUBJECTS.STAND}>
            {showAddStand && (
              <AddStandForm
                isVisible={showAddStand}
                onClose={() => setShowAddStand(false)}
              />
            )}
          </Can>
          <Can I={ACTIONS.READ} a={SUBJECTS.STAND}>
            {showStandsList && (
              <StandsList
                isVisible={showStandsList}
                onClose={() => setShowStandsList(false)}
              />
            )}
          </Can>

          <Can I={ACTIONS.READ} a={SUBJECTS.STATISTICS}>
            {showDriverAnalytics && (
              <DriverAnalytics
                isVisible={showDriverAnalytics}
                onClose={() => setShowDriverAnalytics(false)}
              />
            )}
            {showHistoryAnalytics && (
              <HistoryAnalytics
                isVisible={showHistoryAnalytics}
                onClose={() => setShowHistoryAnalytics(false)}
              />
            )}
          </Can>

          <Can I={ACTIONS.CREATE} a={SUBJECTS.DRIVE}>
            {showRequestDrive && !isLiveMapDemoActive && (
              <RequestDrive
                isVisible={showRequestDrive}
                onClose={() => setShowRequestDrive(false)}
              />
            )}
          </Can>

          <Can I={ACTIONS.ACCESS} a={SUBJECTS.CONFIGURATION}>
            {showSetupConfiguration && (
              <SetupConfiguration
                isVisible={showSetupConfiguration}
                onClose={() => setShowSetupConfiguration(false)}
              />
            )}
            {showTariffs && (
              <TariffsModal
                isVisible={showTariffs}
                onClose={() => setShowTariffs(false)}
              />
            )}
          </Can>

          <Can I={ACTIONS.ACCESS} a={SUBJECTS.AD}>
            {showAds && (
              <AdsModal
                isVisible={showAds}
                onClose={() => setShowAds(false)}
              />
            )}
          </Can>

          {showAddUser && (
            <AddUserForm
              isVisible={showAddUser}
              onClose={() => setShowAddUser(false)}
            />
          )}
          {showUsersList && (
            <UsersList
              isVisible={showUsersList}
              onClose={() => setShowUsersList(false)}
            />
          )}
          {showAssignUserRole && (
            <AssignUserRole
              isVisible={showAssignUserRole}
              onClose={() => setShowAssignUserRole(false)}
            />
          )}
          {showRolesManagement && (
            <RolesManagement
              isVisible={showRolesManagement}
              onClose={() => setShowRolesManagement(false)}
            />
          )}
        </Suspense>
      )}

      {isAuthenticated && isAuthReady && isAdminPage && isPlatformSupervisor && (
        <Suspense fallback={null}>
          {showPermissionsManagement && (
            <PermissionsManagement
              isVisible={showPermissionsManagement}
              onClose={() => setShowPermissionsManagement(false)}
            />
          )}
          {showFeatureFlagsManagement && (
            <FeatureFlagsManagement
              isVisible={showFeatureFlagsManagement}
              onClose={() => setShowFeatureFlagsManagement(false)}
            />
          )}
          {showPrivacyRequests && (
            <PrivacyRequestsList
              isVisible={showPrivacyRequests}
              onClose={() => setShowPrivacyRequests(false)}
            />
          )}
        </Suspense>
      )}
    </AntHeader>
  );
};

export default Header;
